'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useRef, useState } from 'react';
import { extensionFromRef, reportUnsupportedPreview, resolvePreviewIdentity } from '@/lib/file-preview-telemetry';
import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';
import { mediaNeedsTranscode, type GalleryMediaKind } from '@/shared/mobile-service/lib/media-kind';
import type { CollectedMediaInfo, ResolveCollectedMediaInfo } from './collected-media';

type Phase = 'loading' | 'ready' | 'unavailable' | 'unsupported';

/**
 * Inline image or video for an EXIF pin or a message bubble.
 *
 * The frame is reserved immediately. A native file fills it when the bytes
 * arrive. A format the browser cannot play waits on a provenance preview
 * (webp for HEIC). A missing object reads "Not Available"; a format with no
 * transcode reads "Not Supported".
 */
export function CollectedMediaPreview({
  url,
  mime,
  kind,
  filename,
  resolvePreview,
  resolvePreviewInfo,
  onOpen,
  compact = false,
}: {
  url: string;
  mime?: string | null;
  kind: GalleryMediaKind;
  filename?: string | null;
  resolvePreview?: (url: string) => Promise<string | null>;
  /** Wins over `resolvePreview`. Also carries the file id, so an unsupported file can be counted. */
  resolvePreviewInfo?: ResolveCollectedMediaInfo;
  onOpen?: (previewUrl: string | null) => void;
  /** Shorter frame for a map popup. */
  compact?: boolean;
}) {
  const t = useTimePilotT();
  const needsPreview = mediaNeedsTranscode(kind, mime ?? null, url);
  const [phase, setPhase] = useState<Phase>('loading');
  const [displayUrl, setDisplayUrl] = useState<string | null>(needsPreview ? null : url);
  const [lookup, setLookup] = useState<{ requestedUrl: string; info: CollectedMediaInfo } | null>(null);
  const reportedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!needsPreview) {
      setDisplayUrl(url);
      setPhase('loading');
      return;
    }
    let cancelled = false;
    setDisplayUrl(null);
    setPhase('loading');
    const lookupInfo: Promise<CollectedMediaInfo> = resolvePreviewInfo
      ? resolvePreviewInfo(url)
      : (resolvePreview?.(url) ?? Promise.resolve(null)).then((previewUrl) => ({
          status: previewUrl ? 'ok' : 'skipped',
          previewUrl,
          fileUuid: null,
          fileUrl: null,
        }));
    void lookupInfo.then((info) => {
      if (cancelled) return;
      setLookup({ requestedUrl: url, info });
      const preview = info.previewUrl;
      if (preview) {
        setDisplayUrl(preview);
        setPhase('loading');
      } else {
        setPhase('unsupported');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [needsPreview, resolvePreview, resolvePreviewInfo, url]);

  useEffect(() => {
    if (reportedUrlRef.current !== url) reportedUrlRef.current = null;
    if (phase !== 'unsupported' || lookup?.requestedUrl !== url || lookup.info.status !== 'miss') return;
    if (reportedUrlRef.current === url) return;
    reportedUrlRef.current = url;
    reportUnsupportedPreview({
      state: 'mobile_unsupported',
      identity: resolvePreviewIdentity({
        citedRef: url,
        fileUuid: lookup.info.fileUuid,
        fileUrl: lookup.info.fileUrl,
      }),
      mime,
      extension: extensionFromRef(url),
    });
  }, [phase, lookup, url, mime]);

  if (kind !== 'image' && kind !== 'video') return null;
  if (phase === 'unsupported') return <MediaNotice label={t("viewers.notSupported")} compact={compact} />;
  if (phase === 'unavailable') return <MediaNotice label={t("viewers.notAvailable")} compact={compact} />;

  const src = displayUrl ? storageUrlForDisplay(displayUrl) : undefined;
  const label = filename?.trim() || (kind === 'video' ? 'Attached video' : 'Attached image');
  const frame = compact ? 'h-36' : 'h-40';

  return (
    <div className={`relative w-full overflow-hidden rounded-lg bg-black/35 ${frame}`}>
      {phase === 'loading' ? (
        <MediaNotice label="Loading" compact={compact} overlay />
      ) : null}
      {onOpen ? (
        <button
          type="button"
          className="absolute right-1.5 top-1.5 z-10 rounded bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-black/85"
          onClick={(event) => {
            event.stopPropagation();
            onOpen(displayUrl && displayUrl !== url ? displayUrl : null);
          }}
        >{t("viewers.open")}</button>
      ) : null}
      {kind === 'image' && src ? (
        // eslint-disable-next-line @next/next/no-img-element -- object URLs are per-tenant S3 hosts
        <img
          src={src}
          alt={label}
          className={`h-full w-full object-contain ${phase === 'loading' ? 'opacity-0' : ''}`}
          onLoad={() => setPhase('ready')}
          onError={() => setPhase('unavailable')}
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.(displayUrl && displayUrl !== url ? displayUrl : null);
          }}
        />
      ) : null}
      {kind === 'video' && src ? (
        <video
          controls
          playsInline
          preload="metadata"
          src={src}
          aria-label={label}
          className={`h-full w-full object-contain ${phase === 'loading' ? 'opacity-0' : ''}`}
          onLoadedData={() => setPhase('ready')}
          onError={() => setPhase('unavailable')}
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.(displayUrl && displayUrl !== url ? displayUrl : null);
          }}
        />
      ) : null}
    </div>
  );
}

function MediaNotice({
  label,
  compact,
  overlay = false,
}: {
  label: string;
  compact: boolean;
  overlay?: boolean;
}) {
  return (
    <div
      role="status"
      className={`flex items-center justify-center text-xs text-white/70 ${
        overlay
          ? 'pointer-events-none absolute inset-0'
          : `w-full ${compact ? 'h-36' : 'h-40'} rounded-lg bg-black/35`
      }`}
    >
      {label}
    </div>
  );
}
